import React, { FC, useEffect, useRef, useState } from 'react';
import { Column } from 'react-table';
import Table from '../../components/Table';
import {
  Container,
  FormControl,
  Grid,
  InputLabel,
  makeStyles,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@material-ui/core';
import { SubmitHandler, useForm } from 'react-hook-form';
import LoadingButton from '../../components/buttons/LoadingButton';
import * as DoorOpenerAPI from '../../api/dooropener/index';
import * as DoorOpenerModels from '../../api/dooropener/models';
import * as ShortAPI from '../../api/short/index';
import * as ShortModels from '../../api/short/models';
import moment from 'moment';
import 'moment/locale/da';
import NoDataDialog from '../../components/dialogs/ErrorDialog';
import APIRequest from '../../components/helpers/APIRequest';

moment.locale('da');

const useStyles = makeStyles((theme) => ({
  paper: {
    [theme.breakpoints.up('sm')]: {
      padding: '0',
    },
    marginTop: theme.spacing(3),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  buts: {
    width: '100%',
    height: '100%',
  },
  grid: {
    width: '100%', // Fix IE 11 issue.
    marginTop: theme.spacing(3),
  },
  textHelp: {
    marginBottom: '1rem',
  },
  margTop: {
    marginTop: theme.spacing(2),
  },
  formControl: {
    margin: theme.spacing(1),
    minWidth: 120,
  },
  selectEmpty: {
    marginTop: theme.spacing(2),
  },
}));

type FormValues = {
  phone: string;
};

type Props = {};

const DoorOpener: FC<Props> = () => {
  const classes = useStyles();
  const [previousCodes, setPreviousCodes] = useState<DoorOpenerModels.Data[]>(
    []
  );
  const [isPendingPreviousCodes, setIsPendingPreviousCodes] = useState(false);
  const [runGetPreviousCodes, setRunGetPreviousCodes] = useState(false);

  const [runSendTextMessage, setRunSendTextMessage] = useState(false);
  const [isPendingSendTextMessage, setPendingSendTextMessage] = useState(false);
  const [doorOpenerCode, setDoorOpenerCode] =
    useState<DoorOpenerModels.GetCodeResult>({ code: '' });
  const [showErrorMsg, setShowErrorMsg] = React.useState(false);
  const { register, handleSubmit } = useForm<FormValues>();
  const [formData, setFormData] = useState({ locationId: -1, phone: '' });
  const [locationId, setLocationId] = useState(-1);

  const [locations, setLocations] = useState<ShortModels.Location[]>([]);
  const [runGetLocations, setRunGetLocations] = useState(true);
  const [isGetLocationsPending, setGetLocationsPending] = useState(false);

  const [validationErrorMessage, setValidationErrorMessage] = useState('');

  // Necessary for avoiding strikethrough on the location combobox
  const labelRef = useRef<HTMLLabelElement>(null);
  const labelWidth =
    labelRef !== null && labelRef.current !== null
      ? labelRef.current.clientWidth
      : 0;

  const columns: Column<DoorOpenerModels.Data>[] = React.useMemo(
    () => [
      { Header: 'Mobiltelefon', id: '0', accessor: 'phone' },
      { Header: 'Kode', id: '1', accessor: 'code' },
      {
        Header: 'Sendt',
        id: '2',
        accessor: (d) => moment(d.time).format('D. MMM YYYY hh:mm'),
      },
      {
        Header: 'Udløb',
        id: '3',
        accessor: (d) => moment(d.expDate).format('hh:mm'),
      },
    ],
    []
  );

  useEffect(() => {
    if (locations.length > 0) {
      // Set the first location to default
      setLocationId(locations[0].id);
    }
  }, [locations, setLocationId]);

  /**Temporary solution for sms dooropener - Axel Tower is the only one that supports this feature*/
  useEffect(() => {
    if (locations.length > 1) {
      var newArr: ShortModels.Location[] = [];
      var axelT = locations.find(
        (element) => element.name === 'Apcoa 3555 Axel Tower'
      );

      axelT ? newArr.push(axelT) : newArr.push();

      setLocations(newArr);
    }
  }, [locations]);

  const onSubmit: SubmitHandler<FormValues> = async (data) => {
    var p = data.phone.trim();
    var l = p.length;
    if (locationId < 1) {
      setValidationErrorMessage('Du skal vælge en plads.');
    } else if (p === '') {
      setValidationErrorMessage(
        'Husk at skrive et mobilnummer til SMS modtagelse.'
      );
    }else if(l < 8){
      setValidationErrorMessage(
        'Ugyldigt mobilnummer, mindste 8 tal.'
      );
    
    } else {
      setFormData({ locationId: locationId, phone: data.phone });
      setRunSendTextMessage(true);
    }
  };

  const GetPreviousCodes = () => {
    return (
      <APIRequest<DoorOpenerModels.Data[]>
        run={runGetPreviousCodes}
        setRun={setRunGetPreviousCodes}
        pending={isPendingPreviousCodes}
        setIsPending={setIsPendingPreviousCodes}
        req={() => DoorOpenerAPI.getCodes(locationId)}
        setData={setPreviousCodes}
        setShowError={setShowErrorMsg}
      />
    );
  };

  const SendTextMessage = () => {
    const getTextMessage = () => {
      var location = locations.find((l) => l.id === locationId);
      if (location) {
        return location.name + ' port kode: %code%';
      }
      return `Port kode: %code%`;
    };

    return (
      <APIRequest<DoorOpenerModels.GetCodeResult>
        run={runSendTextMessage}
        setRun={setRunSendTextMessage}
        pending={isPendingSendTextMessage}
        setIsPending={setPendingSendTextMessage}
        req={() =>
          DoorOpenerAPI.sendTextMessage({
            body: {
              locationId: formData.locationId,
              phone: formData.phone,
              textMessage: getTextMessage(),
            },
          })
        }
        setData={setDoorOpenerCode}
        setShowError={setShowErrorMsg}
      />
    );
  };

  const GetLocations = () => {
    return (
      <APIRequest<ShortModels.Location[]>
        run={runGetLocations}
        setRun={setRunGetLocations}
        pending={isGetLocationsPending}
        setIsPending={setGetLocationsPending}
        req={ShortAPI.getLocations}
        setData={setLocations}
        setShowError={setShowErrorMsg}
      />
    );
  };

  return (
    <Container maxWidth={false}>
      <GetPreviousCodes />
      <SendTextMessage />
      <GetLocations />
      <div className={classes.paper}>
        <Typography component="h1" variant="h5">
          SMS døråbner
        </Typography>
        <form noValidate onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={2} style={{ padding: 8 }}>
            <Grid item xs={12} sm={4}>
              <FormControl variant="outlined" fullWidth>
                <InputLabel
                  ref={labelRef}
                  shrink
                  htmlFor="door-opener-location"
                >
                  Plads
                </InputLabel>
                <Select
                  labelWidth={labelWidth}
                  id="door-opener-location"
                  value={locationId}
                  onChange={(event: React.ChangeEvent<{ value: unknown }>) => {
                    setLocationId(event.target.value as number);
                  }}
                >
                  {locations.map((location) => {
                    return (
                      <MenuItem
                        key={`LocationId-${location.id}`}
                        value={location.id}
                      >
                        {location.name}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                autoComplete="phone"
                name="phone"
                variant="outlined"
                id="phone"
                label="Mobiltelefon"
                autoFocus
                inputRef={register}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                id="outlined-read-only-input"
                value={doorOpenerCode.code}
                InputProps={{ readOnly: true }}
                label="Dørkode"
                variant="outlined"
              />
            </Grid>
          </Grid>
          {/* </Paper> */}
          <div className={classes.margTop}>
            <LoadingButton
              update={() => {}}
              pending={isPendingSendTextMessage}
              text={'Send'}
            />
          </div>
        </form>

        <div className={classes.paper}>
          <Typography component="h1" variant="h5">
            Tidligere koder
          </Typography>
        </div>
        <div className={classes.paper}>
          <LoadingButton
            update={() => {
              setRunGetPreviousCodes(true);
            }}
            pending={isPendingPreviousCodes}
            text={'Hent tidligere koder'}
          />
        </div>
        <div className={classes.grid}>
          <Table<DoorOpenerModels.Data>
            columns={columns}
            data={previousCodes}
            initialState={{ pageSize: 150 }}
            update={null}
            pending={false}
            text={''}
            clickEvent={() => {}}
          />
        </div>
      </div>

      <NoDataDialog
        open={showErrorMsg}
        onClose={() => {
          setShowErrorMsg(false);
        }}
        title={'Intet svar'}
        text={'Noget gik galt, prøv venligst igen senere.'}
      />
      <NoDataDialog
        open={validationErrorMessage !== ''}
        onClose={() => {
          setValidationErrorMessage('');
        }}
        title={'Kunde ikke sende'}
        text={validationErrorMessage}
      />
    </Container>
  );
};

export default DoorOpener;
